<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  bot: { type: Object, required: true },
});

const phases = defineModel({ type: Array, default: () => [] });

const store = useStore();
const { t } = useI18n();

const tools = useMapGetter('captainTools/getRecords');
const agentBots = useMapGetter('agentBots/getBots');

// Only a bot that runs a session of its own can be handed a conversation.
const handoverOptions = computed(() => [
  { value: '', label: t('AGENT_BOTS.PHASES.SAME_BOT') },
  ...agentBots.value
    .filter(
      bot =>
        bot.id !== props.bot.id &&
        ['flow', 'ai_agent', 'remote'].includes(bot.bot_type)
    )
    .map(bot => ({ value: bot.id, label: bot.name })),
]);

const addPhase = () => {
  phases.value = [...phases.value, { name: '', tools: [], agent_bot_id: '' }];
};

const removePhase = index => {
  phases.value = phases.value.filter((_, position) => position !== index);
};

const updatePhase = (index, changes) => {
  phases.value = phases.value.map((phase, position) =>
    position === index ? { ...phase, ...changes } : phase
  );
};

const toggleTool = (index, toolId) => {
  const current = phases.value[index].tools || [];
  updatePhase(index, {
    tools: current.includes(toolId)
      ? current.filter(item => item !== toolId)
      : [...current, toolId],
  });
};

onMounted(() => {
  store.dispatch('captainTools/getTools');
});
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
  >
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('AGENT_BOTS.PHASES.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('AGENT_BOTS.PHASES.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-for="(phase, index) in phases"
      :key="index"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-weak"
    >
      <div class="flex items-end gap-3">
        <span class="pb-2 text-sm text-n-slate-10">{{ index + 1 }}</span>
        <div class="flex-grow">
          <Input
            :id="`phase-name-${index}`"
            :model-value="phase.name"
            :label="$t('AGENT_BOTS.PHASES.NAME')"
            :placeholder="$t('AGENT_BOTS.PHASES.NAME_PLACEHOLDER')"
            size="sm"
            @update:model-value="updatePhase(index, { name: $event })"
          />
        </div>
        <div class="w-56">
          <label class="mb-1 text-sm text-n-slate-12 block">
            {{ $t('AGENT_BOTS.PHASES.HANDOVER') }}
          </label>
          <ComboBox
            :model-value="phase.agent_bot_id || ''"
            :options="handoverOptions"
            @update:model-value="updatePhase(index, { agent_bot_id: $event })"
          />
        </div>
        <Button
          v-tooltip.top="$t('AGENT_BOTS.PHASES.REMOVE')"
          icon="i-woot-bin"
          slate
          sm
          class="mb-0.5 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          @click="removePhase(index)"
        />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-12">
          {{ $t('AGENT_BOTS.PHASES.TOOLS') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="tool in tools"
            :key="tool.id"
            type="button"
            class="px-2 py-1 text-xs rounded-md outline outline-1"
            :class="
              (phase.tools || []).includes(tool.id)
                ? 'bg-n-brand/10 outline-n-brand text-n-slate-12'
                : 'bg-n-alpha-2 outline-transparent text-n-slate-11'
            "
            @click="toggleTool(index, tool.id)"
          >
            {{ tool.title }}
          </button>
        </div>
        <span class="text-xs text-n-slate-10">
          {{
            (phase.tools || []).length
              ? $t('AGENT_BOTS.PHASES.TOOLS_NARROWED')
              : $t('AGENT_BOTS.PHASES.TOOLS_ALL')
          }}
        </span>
      </div>
    </div>

    <div>
      <Button
        :label="$t('AGENT_BOTS.PHASES.ADD')"
        variant="outline"
        color="slate"
        size="sm"
        @click="addPhase"
      />
    </div>
  </div>
</template>
